import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css, keyframes } from 'styled-components';

import { formatCompactNumber } from 'lib';
import RateItTierIcon from './RateItTierIcon';
import { RATE_IT_COLORS, RATE_IT_LAYOUT, TILE_DIM_OPACITY } from '../constants';

/** One confirm pulse when this tile becomes the pick — the same beat mobile plays. */
const pulse = keyframes`
  0%   { transform: scale(1); }
  40%  { transform: scale(1.08); }
  100% { transform: scale(1); }
`;

const Wrapper = styled.button`
  /* Three tiles across the bar's inner width; the min-width below then guarantees the brief's
     80px floor outright on the narrowest viewport. */
  flex: 1 1 0;
  min-width: ${RATE_IT_LAYOUT.TAP_FLOOR_WIDTH}px;
  /* The whole tile is the tap target, per the brief — except when compact, where the tile is a
     preview of the control rather than the control, and the tap floor does not apply. */
  height: ${(props) =>
    props.$compact ? RATE_IT_LAYOUT.TILE_COMPACT_HEIGHT : RATE_IT_LAYOUT.TILE_HEIGHT}px;
  min-height: ${(props) => (props.$compact ? 0 : RATE_IT_LAYOUT.TAP_FLOOR_HEIGHT)}px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 0;
  overflow: hidden;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  border-radius: ${RATE_IT_LAYOUT.TILE_RADIUS}px;
  background-color: ${RATE_IT_COLORS.TILE_BASE_BG};
  background-image: linear-gradient(
    135deg,
    ${(props) => props.$from} 0%,
    ${(props) => props.$to} 100%
  );
  /* Always 1px so selection swaps a COLOUR, not a width: growing the border would shrink the
     content box and nudge the glyph and label as the ring appears. */
  border: 1px solid
    ${(props) =>
      props.$selected ? RATE_IT_COLORS.TILE_SELECTED_BORDER : RATE_IT_COLORS.TILE_BORDER};
  /* Applied to the whole tile, not the gradient, so the fade takes the label and glyph with it
     and the tile steps back as one object rather than becoming a washed-out fill with
     full-strength text floating on it. */
  opacity: ${(props) => (props.$dimmed ? TILE_DIM_OPACITY : 1)};
  transition:
    opacity 160ms ease,
    border-color 160ms ease;

  ${(props) =>
    props.$pulsing &&
    css`
      animation: ${pulse} 260ms ease-out;
    `}

  &:hover:not(:disabled) {
    opacity: ${(props) => (props.$dimmed ? TILE_DIM_OPACITY : 0.92)};
  }

  /* Keyboard focus must be visible: the bar is reachable by Tab and the tiles are the only
     controls on the card. */
  &:focus-visible {
    outline: 2px solid ${RATE_IT_COLORS.TILE_SELECTED_BORDER};
    outline-offset: 2px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
  }
`;

const Label = styled.span`
  font-family: CeraPro, sans-serif;
  font-size: 14px;
  font-weight: ${(props) => (props.$selected ? 700 : 600)};
  color: ${(props) => props.$col};
  line-height: 1;
`;

/**
 * Label and counter as one centred row, sharing a baseline so the smaller count reads as part
 * of the label rather than dropping below it. Mirrors mobile's `RateItTile`.
 */
const LabelWrap = styled.span`
  display: inline-flex;
  align-items: baseline;
`;

const Count = styled.span`
  margin-left: ${RATE_IT_LAYOUT.TILE_COUNT_GAP}px;
  font-family: CeraPro, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: ${(props) => props.$col};
  line-height: 1;
  white-space: nowrap;
`;

/**
 * One of the three sentiment tiles — the brief's `RateIt_Tile_Base`, with Solid/Great/Epic being
 * three rows of `RATE_IT_TIERS` rather than three components.
 *
 * THE TILE IS FILLED WITH ITS TIER'S GRADIENT AT REST. It is not a grey slab that colours in on
 * tap: three coloured tiles name the three choices on sight, where an unfilled bar makes the fan
 * tap one to find out what tapping does — on a surface where the tap is the entire interaction
 * and is irreversible. What selection changes is the CONTRAST: the pick gets a ring and a pulse
 * while the other two fade to `TILE_DIM_OPACITY`.
 *
 * `dimmed` is passed in rather than derived here because a tile only knows its own selected
 * flag, and "nobody has rated yet" must not look like "someone else was picked".
 *
 * `disabled` locks the WHOLE bar, not just the tapped tile — set once the fan has scrolled away
 * from a moment they rated (until then the pick can change). The opposite of Tag It, where a
 * wrong guess locks only the tile you guessed.
 */
const RateItTile = ({ tier, selected, dimmed, disabled, compact, onPress, count }) => {
  const { t } = useTranslation();
  // Looked up by the tier's OWN id, so the label follows the rating value the backend enum
  // defines rather than a second list that could fall out of step with it. `tier.label` is the
  // English fallback and the reason `RATE_IT_TIERS` still carries one.
  const label = t(`tiri.rateIt.ratings.${tier.id}`, tier.label);
  // Pulse only on the false -> true edge; re-renders while selected must not replay it.
  const [pulsing, setPulsing] = useState(false);
  const wasSelected = useRef(selected);

  useEffect(() => {
    if (selected && !wasSelected.current) {
      setPulsing(true);
      const timer = setTimeout(() => setPulsing(false), 300);
      return () => clearTimeout(timer);
    }
    wasSelected.current = selected;
    return undefined;
  }, [selected]);

  return (
    <Wrapper
      type="button"
      disabled={disabled}
      onClick={() => onPress(tier.id)}
      aria-pressed={selected}
      // The count is read out with the label, since it is drawn as part of it.
      aria-label={count === undefined ? label : `${label}, ${count}`}
      $from={tier.gradient[0]}
      $to={tier.gradient[1]}
      $selected={selected}
      $dimmed={dimmed}
      $pulsing={pulsing}
      $compact={compact}
    >
      {/* Dropped when compact: at 34px the glyph and the label fight for the same room, and the
          label is the half that names the tier. */}
      {compact ? null : (
        <RateItTierIcon
          rating={tier.id}
          size={RATE_IT_LAYOUT.TILE_ICON_HEIGHT}
          // White, against the tier-tinted label below it: the glyphs are what make the three
          // tiles one set, and tinting each to its own tier would break that.
          col={RATE_IT_COLORS.TILE_LABEL}
        />
      )}
      <LabelWrap>
        <Label $col={tier.labelColor} $selected={selected}>
          {label}
        </Label>
        {/* How many fans picked this tier. Omitted (no tallies on the item), no counter. */}
        {count === undefined ? null : (
          <Count $col={tier.labelColor} aria-hidden="true">
            {formatCompactNumber(count)}
          </Count>
        )}
      </LabelWrap>
    </Wrapper>
  );
};

export default RateItTile;
